import type { ControllerFlowAPI, HttpResponse } from '@wix/yoshi-flow-editor';
import { fetchPost, FetchPostError } from '../../common/actions/fetch-post';
import {
  createNotFoundPageRouter,
  createTooManyRequestsPageRouter,
} from '../../common/controller/create-router';
import type { PlatformApi } from '../../common/controller/platform-api';
import { Router } from '../../common/router';
import {
  POST_PAGE_NOT_FOUND,
  POST_PAGE_TOO_MANY_REQUESTS,
} from '../../common/services/detect-route';
import { setIsPostInPreview } from '../../common/store/is-post-in-preview/set-is-post-in-preview-action';
import { type AppStore, type ControllerConfig, type RouteResolverFn } from '../../common/types';
import {
  ROUTE_404,
  ROUTE_429,
  ROUTE_EDIT_COMMENT,
  ROUTE_EDIT_COMMENT_ADVANCED_SLUG,
  ROUTE_LAST_POST,
  ROUTE_LOGIN,
  ROUTE_POST,
  ROUTE_POST_ADVANCED_SLUG,
  ROUTE_PREVIEW_POST,
} from '../constants/routes';
import { customRouteHandler } from './route-handlers/custom-route-handler';
import { createLastPostPageRouter } from './route-handlers/last-post-page-router-handler';
import { createPostPageRouter } from './route-handlers/post-page-router-handler';

const createPostPreviewPageRouter =
  (
    store: AppStore,
    platformApi: PlatformApi,
  ): RouteResolverFn<{ postId: string; instance: string }> =>
  async ({ params }, redirect) => {
    const postId = params.postId;

    return store
      .dispatch(
        fetchPost(postId, {
          includeDraft: true,
          instance: params.instance,
          previewTextParagraph: platformApi.location?.query?.previewTextParagraph,
        }),
      )
      .then(() => store.dispatch(setIsPostInPreview(true)))
      .catch((error: HttpResponse) => {
        if (error.status === 401) {
          return redirect(`/login?redirect=/${postId}`);
        }
        if (error.status === 404 || error instanceof FetchPostError) {
          return redirect(ROUTE_404);
        }
        throw error;
      });
  };

type CreateRouterParams = {
  store: AppStore;
  platformApi: PlatformApi;
  flowAPI: ControllerFlowAPI;
  slotAPIFactory: Record<string, any>;
  controllerConfig: ControllerConfig;
};

export const createRouter = ({
  store,
  platformApi,
  flowAPI,
  slotAPIFactory,
  controllerConfig,
}: CreateRouterParams) => {
  const router = new Router();
  const { isEditor, isPreview } = flowAPI.environment;

  if (isEditor || isPreview) {
    router.add(ROUTE_LAST_POST, createLastPostPageRouter({ store }));
  }

  router.add(
    ROUTE_404,
    createNotFoundPageRouter(store, platformApi, POST_PAGE_NOT_FOUND, ROUTE_404),
  );
  router.add(
    ROUTE_429,
    createTooManyRequestsPageRouter(store, platformApi, POST_PAGE_TOO_MANY_REQUESTS, ROUTE_429),
  );
  router.add(ROUTE_LOGIN, () => {});
  router.add(ROUTE_PREVIEW_POST, createPostPreviewPageRouter(store, platformApi));
  router.add(ROUTE_EDIT_COMMENT, () => {});

  for (const route of [ROUTE_POST, ROUTE_POST_ADVANCED_SLUG]) {
    router.add(
      route,
      createPostPageRouter({
        store,
        platformApi,
        flowAPI,
        slotAPIFactory,
        controllerConfig,
      }),
      {
        preFetch: true,
      },
    );
  }

  router.add(ROUTE_EDIT_COMMENT_ADVANCED_SLUG, () => {});
  router.addCustomRouteHandler(() => customRouteHandler(platformApi));
  router.fallback(ROUTE_404);
  return router;
};
